import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
    Box,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Checkbox,
    FormControlLabel,
    TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useData } from "@pure-ptr/react";
import CloseIcon from '@mui/icons-material/Close';
import { getGlobalFeeScheduleCopyPreview, copyGlobalFeeSchedule } from '../../store/api/feeScheduleV2';
import { fetchGPCILocalityData, GPCILocalityData } from '../../store/api/globalFeeSchedule';
import { getStartDate, getEndDate, asDate } from '../../util/date';
import { Loadable } from '../../common';
import CopyGlobalFeeScheduleItem from './CopyGlobalFeeScheduleItem';
import { successToastify, errorToastify,infoToastify } from '../../util/toast';
import StateDropdown from '../contractsPage/RulesEditPage/components/StateDropdown';
import GPCIDropdown from '../contractsPage/RulesEditPage/components/GPCIDropdown';
import LocalityDropdown from '../contractsPage/RulesEditPage/components/LocalityDropdown';
import DateRangeControl from '../../common/dateRangeControl/DateRangeControl';
import { generateErrorToaster } from '../ClaimAnalysisPage/ClaimAnalysisPage';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogContent: {
        minHeight: '400px',
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', 
    },
    scrollableContent: {
        overflowY: 'auto',
        paddingRight: theme.spacing(1),
        maxHeight: '45vh', 
        flexGrow: 1,
        paddingBottom: theme.spacing(20),
    },
    stepper: {
        padding: theme.spacing(3, 0, 5),
    },
    fieldContainer: {
        marginTop: '80px',
        padding: theme.spacing(2),
        border: '1px solid #e0e0e0',
        borderRadius: 4
    }
}));

interface CopyGlobalFeeScheduleDialogProps {
    open: boolean;
    rowData: any;
    onClose: () => void;
    onCopy: () => void;
}

const STEPS = ['Configuration', 'Review & Select'];

export function CopyGlobalFeeScheduleDialog({ open, rowData, onClose, onCopy }: CopyGlobalFeeScheduleDialogProps) {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    
    // Step 0 states
    const [formValues, setFormValues] = useState({
        startDate: getStartDate('Last12Months'),
        endDate: getEndDate('Last12Months'),
        relativeFilter: 'Last12Months',
        state: null as string | null,
        gpci: null as string | null,
        locality: null as string | null
    });
    const [gpciLocalityData, setGpciLocalityData] = useState<GPCILocalityData[]>([]);

    // Step 1 states
    const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
    const [newFeeScheduleName, setNewFeeScheduleName] = useState<string>('');
    const [itemExpirationDates, setItemExpirationDates] = useState<Record<string, string>>({});
    const [payloadToCopy, setPayloadToCopy] = useState<any | null>(null);

    const isRBRVS = rowData?.name?.toUpperCase() === 'RBRVS' || rowData?.name?.toUpperCase() === 'ANESTHESIA';
    const isDME = rowData?.name?.toUpperCase() === 'DME';
    const isAnesthesia = rowData?.name?.toUpperCase() === 'ANESTHESIA';

    // Fetch regional data
    useEffect(() => {
        if ((isRBRVS || isDME || isAnesthesia) && open) {
            fetchGPCILocalityData()
                .then(data => setGpciLocalityData(data))
                .catch(err => console.error('Failed to load regional data', err));
        }
    }, [isRBRVS, isDME, isAnesthesia, open]);

    // Fetch preview data for Step 1
    const { data: groupedItems, isPending } = useData(async () => {
        if (!rowData || !open || activeStep !== 1) return [];
        try {
            const options = {
                startDate: formValues.startDate,
                endDate: formValues.endDate,
                state: formValues.state,
                gpci: formValues.gpci,
                locality: formValues.locality
            };
            const globalFeeSchedulePreviewData = await getGlobalFeeScheduleCopyPreview(rowData.id, options);
            return globalFeeSchedulePreviewData;
        } catch (error) {
            console.error("Error fetching preview data:", error);
            return [];
        }
    }, [rowData?.id, open, activeStep]);
    
    const itemsList = useMemo(() => {
        if (!groupedItems) return [];
        if (Array.isArray(groupedItems)) return groupedItems;
        if (typeof groupedItems === 'object' && Array.isArray((groupedItems as any).items)) return (groupedItems as any).items;
        return [];
    }, [groupedItems]);

    const { isPending: isSaving } = useData(async () => {
        if (!payloadToCopy) return;
        try {
            await copyGlobalFeeSchedule(payloadToCopy);
            infoToastify(<><b>Copying Global FeeSchedules </b><br/>You'll be notified when it's complete.</>);
            onCopy(); // Callback to parent to refresh
            setPayloadToCopy(null);
            handleClose();
        } catch (error: any) {
            generateErrorToaster(error,error?.payload?.error)
            setPayloadToCopy(null);
        }
    }, [payloadToCopy]);

    useEffect(() => {
        if (open && itemsList?.length > 0 && activeStep === 1) {
            const initialSelected = {};
            const initialExpirations = {};
            
            // Set a default name if not already set
            if (!newFeeScheduleName) {
                const apiName = (groupedItems as any)?.newFeeScheduleName;
                setNewFeeScheduleName(apiName || rowData?.name || '');
            }

            itemsList.forEach((item, index) => {
                initialSelected[index] = false;
                initialExpirations[index] = item.expirationDate ? moment(item.expirationDate).format('YYYY-MM-DD') : '';
            });
            setSelectedItems(initialSelected);
            setItemExpirationDates(initialExpirations);
        }
    }, [open, itemsList, activeStep, rowData, groupedItems]);

    const handleClose = useCallback(() => {
        setActiveStep(0);
        setSelectedItems({});
        setNewFeeScheduleName('');
        setItemExpirationDates({});
        setPayloadToCopy(null);
        setFormValues({
            startDate: getStartDate('Last12Months'),
            endDate: getEndDate('Last12Months'),
            relativeFilter: 'Last12Months',
            state: null,
            gpci: null,
            locality: null
        });
        onClose();
    }, [onClose]);

    const handleNext = () => {
        setActiveStep(1);
    }

    const handleBack = () => {
        setActiveStep(0);
    }

    const handleToggle = useCallback((index) => {
        setSelectedItems(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    }, []);

    const handleNameChange = useCallback((value) => {
        setNewFeeScheduleName(value);
    }, []);

    const handleExpirationDateChange = useCallback((index, value) => {
        setItemExpirationDates(prev => ({
            ...prev,
            [index]: value
        }));
    }, []);

    const allSelected = useMemo(() => {
        if (!itemsList || itemsList.length === 0) return false;
        return itemsList.every((_, index) => !!selectedItems[index]);
    }, [itemsList, selectedItems]);

    const handleSelectAll = useCallback(() => {
        const newValue = !allSelected;
        const updated = {};
        itemsList?.forEach((_, index) => {
            updated[index] = newValue;
        });
        setSelectedItems(updated);
    }, [allSelected, itemsList]);

    const dateRangeError = useMemo(() => {
        if (!formValues.startDate || !formValues.endDate) return null;
        const start = moment(formValues.startDate);
        const end = moment(formValues.endDate);
        const diffDays = Math.abs(end.diff(start, 'days'));
        if (diffDays > 365) {
            return "Please select a date range within 1 year (maximum 365 days).";
        }
        return null;
    }, [formValues.startDate, formValues.endDate]);

    const isNextDisabled = useMemo(() => {
        if (!formValues.startDate || !formValues.endDate || !!dateRangeError) return true;
        if (isRBRVS || isAnesthesia) {
            return !formValues.state || !formValues.gpci || !formValues.locality;
        }
        if (isDME) {
            return !formValues.state;
        }
        return false;
    }, [formValues, isRBRVS, isDME, isAnesthesia, dateRangeError]);

    const validationSummary = useMemo(() => {
        const selectedIndices = Object.keys(selectedItems).filter(index => selectedItems[index] === true);
        if (selectedIndices.length === 0) return null;

        let nameMissing = !newFeeScheduleName?.trim();
        let emptyExpirations = 0;
        let invalidExpirations = 0;
        
        selectedIndices.forEach(index => {
            const expDate = itemExpirationDates[index];
            const effDate = itemsList?.[index]?.effectiveDate;

            if (!expDate) emptyExpirations++;
            else if (effDate && moment(expDate).isBefore(moment(effDate).startOf('day'))) invalidExpirations++;
        });

        if (!nameMissing && emptyExpirations === 0 && invalidExpirations === 0) return null;

        const errors = [];
        if (nameMissing) errors.push(`Fee schedule name is required`);
        if (emptyExpirations > 0) errors.push(`${emptyExpirations} item(s) missing expiration date`);
        if (invalidExpirations > 0) errors.push(`${invalidExpirations} item(s) invalid date order`);

        return errors;
    }, [selectedItems, newFeeScheduleName, itemExpirationDates, groupedItems]);

    const isCopyDisabled = useMemo(() => {
        const selectedIndices = Object.keys(selectedItems).filter(index => selectedItems[index] === true);
        if (selectedIndices.length === 0) return true;
        return !!validationSummary;
    }, [selectedItems, validationSummary]);

    const handleCopy = () => {
        if (isCopyDisabled) return;

        const selectedRecords = (itemsList || [])
            .map((item, index) => ({
                globalFeeScheduleId: item.globalFeeScheduleId || item.id,
                state: item.state,
                gpci: item.gpci,
                locality: item.locality,
                effectiveDate: item.effectiveDate,
                expirationDate: itemExpirationDates[index] || null
            }))
            .filter((_, index) => selectedItems[index]);

        const payload = {
            newFeeScheduleName: newFeeScheduleName.trim(),
            items: selectedRecords
        };

        setPayloadToCopy(payload);
    };

    const renderStepContent = () => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                {/* Step 0: Configuration */}
                <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>Select Date Range</Typography>
                        <DateRangeControl
                            filterName="Date Range"
                            relativeFilterValue={formValues.relativeFilter}
                            setDates={(start, end, enabled, field, relFilter) => {
                                setFormValues(prev => ({ 
                                    ...prev, 
                                    startDate: start, 
                                    endDate: end, 
                                    relativeFilter: relFilter 
                                }));
                            }}
                            defaultControlEnabled={true}
                            enabledSwitch={false}
                        />
                        {dateRangeError && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                {dateRangeError}
                            </Typography>
                        )}

                        {(isRBRVS || isDME || isAnesthesia) && (
                            <Box className={classes.fieldContainer} >
                                <Typography variant="subtitle2" gutterBottom>
                                    Regional Configurations {isDME ? '(DME)' : isAnesthesia ? '(Anesthesia)' : '(RBRVS)'}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={(isRBRVS || isAnesthesia) ? 4 : 12}>
                                        <StateDropdown
                                            scheduleId={rowData?.id}
                                            value={formValues.state}
                                            onChange={(val) => {
                                                setFormValues(prev => ({ ...prev, state: val, gpci: null, locality: null }));
                                            }}
                                            required
                                        />
                                    </Grid>
                                    {(isRBRVS || isAnesthesia) && (
                                        <>
                                            <Grid item xs={12} md={4}>
                                                <GPCIDropdown
                                                    selectedState={formValues.state}
                                                    gpciLocalityData={gpciLocalityData}
                                                    value={formValues.gpci}
                                                    onChange={(val) => {
                                                        setFormValues(prev => ({ ...prev, gpci: val, locality: null }));
                                                    }}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <LocalityDropdown
                                                    selectedState={formValues.state}
                                                    selectedGpci={formValues.gpci}
                                                    gpciLocalityData={gpciLocalityData}
                                                    value={formValues.locality}
                                                    onChange={(val) => setFormValues(prev => ({ ...prev, locality: val }))}
                                                    required
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Step 1: Review & Select */}
                <Box sx={{ 
                    display: activeStep === 1 ? 'block' : 'none', 
                }}>
                    <Loadable loading={!!isPending}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Select the Global Fee Schedules you want to copy into Local Fee Schedules. You can edit the expiration date for each schedule before copying.
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <TextField
                                label="New Fee Schedule Name"
                                fullWidth
                                variant="outlined"
                                value={newFeeScheduleName}
                                onChange={(e) => handleNameChange(e.target.value)}
                                error={activeStep === 1 && !newFeeScheduleName?.trim()}
                                helperText={activeStep === 1 && !newFeeScheduleName?.trim() ? "Name is required" : ""}
                                required
                            />
                        </Box>

                        {validationSummary && (
                            <Box sx={{ 
                                bgcolor: '#fdeded', 
                                borderLeft: '4px solid #d32f2f', 
                                p: 1.5, 
                                mb: 2, 
                                borderRadius: '0px 4px 4px 0px' 
                            }}>
                                <Typography color="#5f2120" variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                                    Validation Errors
                                </Typography>
                                <Box sx={{ pl: 3.5 }}>
                                    {validationSummary.map((err, i) => (
                                        <Typography key={i} color="#5f2120" variant="body2" sx={{ fontSize: '0.8rem' }}>
                                            • {err}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        
                        {itemsList && itemsList.length > 0 && (
                            <Box sx={{ borderBottom: '1px solid #e0e0e0', pb: 1, mb: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={!allSelected && Object.values(selectedItems).some(Boolean)}
                                            onChange={handleSelectAll}
                                            color="primary"
                                        />
                                    }
                                    label={<Typography sx={{ fontWeight: 600 }}>Select All</Typography>}
                                />
                            </Box>
                        )}

                        <Box className={classes.scrollableContent} mt={2}>
                            {(!itemsList || itemsList.length === 0) ? (
                                <Typography align="center" sx={{ mt: 4 }}>No records found for the selected criteria.</Typography>
                            ) : (
                                itemsList.map((item, index) => {
                                    const expDate = itemExpirationDates[index];
                                    const effDate = item.effectiveDate;
                                    
                                    let expirationError = "";
                                    if (selectedItems[index]) {
                                        if (!expDate) expirationError = "Expiration date is required";
                                        else if (effDate && moment(expDate).isBefore(moment(effDate).startOf('day'))) {
                                            expirationError = `Cannot be before effective date (${asDate(effDate)})`;
                                        }
                                    }

                                    return (
                                        <CopyGlobalFeeScheduleItem
                                            key={index}
                                            item={item}
                                            isSelected={!!selectedItems[index]}
                                            expirationDate={expDate}
                                            onToggle={() => handleToggle(index)}
                                            onExpirationDateChange={(val) => handleExpirationDateChange(index, val)}
                                            isLast={index === itemsList.length - 1}
                                            expirationError={expirationError}
                                        />
                                    );
                                })
                            )}
                        </Box>
                        </Box>
                    </Loadable>
                </Box>
            </Box>
        );
    }

    return (
        <Dialog maxWidth='md' fullWidth open={open} onClose={isSaving ? null : handleClose}>
            <DialogTitle>
                <Grid container>
                    <Grid item xs={11}>
                        <Typography variant="h6">
                            Copy Global Fee Schedule: {rowData?.name}
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        {!isSaving && (
                            <IconButton
                                aria-label="close"
                                className={classes.closeButton}
                                onClick={handleClose}
                                size="large">
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers className={classes.dialogContent}>
                <Stepper activeStep={activeStep} className={classes.stepper} alternativeLabel>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {renderStepContent()}
            </DialogContent>
            <DialogActions className='p-4'>
                <Button variant="outlined" onClick={handleClose} disabled={!!isSaving} sx={{ mr: 1 }}>
                    Cancel
                </Button>
                {activeStep === 0 ? (
                    <Button
                        onClick={handleNext}
                        variant="contained"
                        color="primary"
                        disabled={isNextDisabled}
                    >
                        Next
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={handleBack}
                            variant="outlined"
                            disabled={!!isSaving}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={handleCopy}
                            variant="contained"
                            color="primary"
                            disabled={!!isCopyDisabled || !!isSaving || !!isPending}
                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{ ml: 1 }}
                        >
                            {isSaving ? 'Copying...' : 'Copy'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
